import React from 'react';
import { Link } from 'react-router-dom';
import AstraAvatar from '../../concierge/AstraAvatar';
import { PLANNING, PLANNING_DATA } from '../../../content/landingHome';
import { papers } from '../../../data/resources';
import StatBand, { STAT_ITEM, STAT_NUMERAL, STAT_TINT } from '../../StatBand';
import { Eyebrow, StartFree } from './parts';

// "And we plan the rest": the planning half of the product, compact. Six
// lines to the guides that explain each, Astra in one sentence, and the
// guide count. The count is the papers THIS visitor can open, and it is left
// out until the flags have resolved, rather than shown as a guess.

/** The data behind the plan (#2527): five catalog figures, each with what it
 * means for a parent. On a phone the figure and its label share a line and
 * the reason runs full width under them, which keeps the band short; one
 * line of five on a wide screen. Full-bleed on the shared green band since
 * #4598 (founder 2026-10-17), so the section around it splits its column. */
export function PlanningData({ copy = PLANNING_DATA }) {
    if (!copy?.tiles?.length) return null;
    return (
        <StatBand as="div" data-landing-section="planning_data" data-testid="landing-planning-data" className="mb-8 px-4 py-8 sm:px-6 lg:px-8 lg:py-12">
            <div className="mx-auto max-w-6xl">
                <h3 className={`mb-3 text-center text-xs font-bold uppercase tracking-wider ${STAT_TINT}`}>{copy.label}</h3>
                <ul role="list" className="m-0 mt-1 grid list-none grid-cols-1 gap-y-4 p-0 sm:grid-cols-2 sm:gap-x-6 sm:gap-y-5 lg:grid-cols-4">
                    {copy.tiles.map((t) => (
                        <li key={t.key} data-testid={`landing-planning-data-${t.key}`} className={STAT_ITEM}>
                            <div className="flex items-baseline gap-x-2.5 sm:block">
                                <p className={`shrink-0 ${STAT_NUMERAL} text-3xl sm:text-4xl`}>{t.figure}</p>
                                <p className="min-w-0 text-sm font-semibold leading-snug text-white [text-wrap:balance] sm:mt-2 lg:min-h-[2.75em]">{t.label}</p>
                            </div>
                            <p className={`mt-1 text-sm leading-snug ${STAT_TINT} [text-wrap:pretty]`}>{t.why}</p>
                        </li>
                    ))}
                </ul>
            </div>
        </StatBand>
    );
}

// `hideAstra` (#2725): with the open chat leading the page, Astra's box here
// is merged into the hero, so it is not said twice.
export default function PlanningSection({ onStart, hideAstra = false }) {
    const guideCount = papers.length;
    return (
        <section id="plan" data-landing-section="planning" data-testid="landing-planning" className="scroll-mt-20 bg-[#FDFCF7] py-10 lg:py-20">
            <div className="px-4 sm:px-6 lg:px-8"><div className="mx-auto max-w-6xl">
                <div className="mx-auto mb-8 max-w-3xl text-center">
                    <Eyebrow>{PLANNING.eyebrow}</Eyebrow>
                    <h2 className="[text-wrap:balance] mb-3 font-serif text-3xl font-bold leading-tight text-[#1A4D2E] sm:text-4xl">{PLANNING.title}</h2>
                    <p className="mx-auto max-w-2xl text-lg leading-relaxed text-[#4A4A4A]">{PLANNING.sub}</p>
                </div>
            </div></div>
            <PlanningData />
            <div className="px-4 sm:px-6 lg:px-8"><div className="mx-auto max-w-6xl">
                <ul role="list" className="m-0 grid list-none gap-x-6 p-0 sm:grid-cols-2 lg:grid-cols-3">
                    {PLANNING.items.map((p) => (
                        <li key={p.title} className="min-w-0 border-t border-[#E0DED8] py-3">
                            <Link to={p.to} data-cta="planning_guide" className="group block min-h-[44px]">
                                <span className="font-serif text-xl font-bold text-[#1A4D2E] group-hover:underline">{p.title}</span>
                                <span className="mt-0.5 block text-base leading-snug text-[#4A4A4A] [text-wrap:pretty]">{p.body}</span>
                            </Link>
                        </li>
                    ))}
                </ul>
                {!hideAstra && <div className="mx-auto mt-8 flex max-w-2xl items-start gap-4 rounded-2xl border border-[#E0DED8] bg-white p-5 sm:gap-5 sm:p-6" data-testid="landing-astra">
                    <div className="shrink-0"><AstraAvatar size={52} /></div>
                    <div className="min-w-0">
                        <h3 className="[text-wrap:balance] font-serif text-2xl font-bold leading-tight text-[#1A4D2E] sm:text-3xl">{PLANNING.astraHeading}</h3>
                        <p className="mt-2 text-base leading-relaxed text-[#2C2C2C]">
                            <strong className="text-[#1A4D2E]">{PLANNING.astra}</strong> {PLANNING.astraRest}
                        </p>
                    </div>
                </div>}
                <p className="mt-5 text-center text-base text-[#4A4A4A]">
                    {`${guideCount} free guides on how admissions works, no account needed. `}
                    <Link to="/resources" data-cta="planning_resources" className="font-semibold text-[#1A4D2E] underline decoration-[#CFE3D4] underline-offset-4">Browse them</Link>
                </p>
                <div className="mt-6 flex justify-center">
                    <StartFree onStart={onStart} cta="planning_start_free" size="md" />
                </div>
            </div></div>
        </section>
    );
}
